/**
 * Tutorial cues — the short "who · what to press" chips the HUD pins next
 * to the object a tutorial step is about (see HUD.setCue).
 *
 * They replace full-sentence prompts in a wrapped panel over the middle
 * of the screen ("Player 2 (Basil): use Arrows to walk onto the brass
 * plate. Press 3 to anchor him so he holds it down while Minna works."),
 * which covered the cats and the platforms they were jumping to.
 *
 * PURE — no Babylon imports. Cues name logical actions; the key labels
 * are resolved at render time for the live input mode, active cat and
 * control scheme, so a cue never tells a solo player to press a key that
 * belongs to the other cat.
 */
import { controllerLabels, type ControlAction, type ControlGlyphScheme } from "./ControlGlyphs";

export type CueCat = "minna" | "basil" | "both";

export type CuePart = { text: string } | { action: ControlAction; hold?: boolean };

export interface CueDef {
  cat: CueCat;
  parts: CuePart[];
}

/** A cue resolved for display: text runs and key caps, in order. */
export type CueSegment =
  | { kind: "text"; text: string }
  | { kind: "key"; label: string }
  | { kind: "cat"; cat: CueCat };

export interface CueContext {
  soloMode: boolean;
  /** InputManager.activePlayer: 1 = Minna, 2 = Basil. */
  activePlayer: 1 | 2;
  scheme: ControlGlyphScheme;
}

export type TutorialStep = "plate" | "dial" | "crystal" | "portal" | "complete";

/** Level 0 step cues (TutorialGate). Two or three words per action. */
export const TUTORIAL_STEP_CUES: Record<TutorialStep, CueDef | null> = {
  plate: { cat: "basil", parts: [{ text: "onto the plate," }, { action: "anchor" }, { text: "anchor" }] },
  dial: { cat: "basil", parts: [{ action: "turnStart" }, { text: "spin," }, { action: "rhythmHit" }, { text: "on the beat" }] },
  crystal: { cat: "minna", parts: [{ action: "aura" }, { text: "reveal," }, { action: "purr", hold: true }, { text: "purr" }] },
  portal: { cat: "both", parts: [{ text: "into the portal together" }] },
  complete: null,
};

const CAT_NAME: Record<Exclude<CueCat, "both">, string> = { minna: "Minna", basil: "Basil" };

/**
 * Keyboard keys for an action. Minna's abilities are Q/E/F and Basil's
 * the number row in both modes; movement depends on the mode: solo drives
 * the active cat with WASD/Space, co-op gives Basil the arrows + Enter.
 */
export function keyboardKeys(action: ControlAction, cat: Exclude<CueCat, "both">, soloMode: boolean): string[] {
  const basilArrows = cat === "basil" && !soloMode;
  switch (action) {
    case "move": return basilArrows ? ["←", "→"] : ["A", "D"];
    case "jump": return basilArrows ? ["Enter"] : ["Space"];
    case "switchCat": return ["Tab"];
    case "aura": return ["Q"];
    case "shadow": return ["E"];
    case "purr": return ["F"];
    case "turnStart": return ["1"];
    case "rhythmHit": return ["2"];
    case "anchor": return ["3"];
    case "leap": return ["4"];
    // Minna picks up with E where a level gives her something to carry
    // (L1 food); Basil's interact is comma everywhere.
    case "interact": return cat === "minna" ? ["E"] : [","];
    // Co-op chords: Minna's key + Basil's key, pressed together.
    case "resonantDrop": return ["F", "M"];
    case "chronosBubble": return ["G", "N"];
    case "pause": return ["Esc"];
    default: return [];
  }
}

function keysFor(action: ControlAction, cat: Exclude<CueCat, "both">, ctx: CueContext): string[] {
  return ctx.scheme === "keyboard" ? keyboardKeys(action, cat, ctx.soloMode) : controllerLabels(action, ctx.scheme);
}

/**
 * Resolve a cue for display. In solo mode, a cue for the cat you are NOT
 * controlling becomes "[Tab] switch to <cat>" — the only useful thing to
 * do next — instead of listing keys that would move the wrong cat.
 */
export function resolveCue(def: CueDef, ctx: CueContext): CueSegment[] {
  const active: Exclude<CueCat, "both"> = ctx.activePlayer === 2 ? "basil" : "minna";
  if (ctx.soloMode && def.cat !== "both" && def.cat !== active) {
    return [
      ...keysFor("switchCat", active, ctx).map((label): CueSegment => ({ kind: "key", label })),
      { kind: "text", text: `switch to ${CAT_NAME[def.cat]}` },
    ];
  }
  const actor: Exclude<CueCat, "both"> = def.cat === "both" ? active : def.cat;
  const out: CueSegment[] = [{ kind: "cat", cat: def.cat }];
  for (const part of def.parts) {
    if ("text" in part) {
      out.push({ kind: "text", text: part.text });
    } else {
      if (part.hold) out.push({ kind: "text", text: "hold" });
      for (const label of keysFor(part.action, actor, ctx)) out.push({ kind: "key", label });
    }
  }
  return out;
}

/**
 * One-line movement cue shown at the start of Level 0 until the player
 * has moved and jumped. Co-op names both players' keys; solo teaches Tab.
 */
export function resolveControlsCue(ctx: CueContext): CueSegment[] {
  const key = (label: string): CueSegment => ({ kind: "key", label });
  const text = (t: string): CueSegment => ({ kind: "text", text: t });
  if (ctx.scheme !== "keyboard") {
    return [
      ...controllerLabels("move", ctx.scheme).map(key), text("move"),
      ...controllerLabels("jump", ctx.scheme).map(key), text("jump"),
      ...(ctx.soloMode ? [...controllerLabels("switchCat", ctx.scheme).map(key), text("switch cat")] : []),
    ];
  }
  if (ctx.soloMode) {
    return [key("A"), key("D"), text("move"), key("Space"), text("jump"), key("Tab"), text("switch cat"), key("F2"), text("two players")];
  }
  return [
    { kind: "cat", cat: "minna" }, key("A"), key("D"), key("Space"),
    { kind: "cat", cat: "basil" }, key("←"), key("→"), key("Enter"),
    key("F2"), text("one player"),
  ];
}
